import * as React from "react";
import type { UsersShopCalendarDayType } from "@graphql/__generated__/types";
import { Calendar } from "../Calendar";
import { useShopCardLargeFooterCalendarContainer_ShopCalendarDaysLazyQuery } from "./__generated__/queries";
import type { ShopCardLarge_ShopFragment as ShopListItemFragment } from "../../__generated__/fragments";
import type { FC } from "react";

type Props = {
  shop: ShopListItemFragment;
};

// shops/{shopUuid}のカレンダーが56日表示のため、表示数を合わせて取得する
const ALL_CALENDAR_DAYS_FETCH_COUNT = 31;

// dateをキーにして重複をなくしながらマージする
const mergeCalendarDays = (
  currentCalendarDays: UsersShopCalendarDayType[],
  nextCalendarDays: UsersShopCalendarDayType[]
): UsersShopCalendarDayType[] => {
  const calendarDayMap = new Map<string, UsersShopCalendarDayType>();

  currentCalendarDays.forEach((calendarDay) => {
    calendarDayMap.set(calendarDay.date, calendarDay);
  });

  nextCalendarDays.forEach((calendarDay) => {
    calendarDayMap.set(calendarDay.date, calendarDay);
  });

  return Array.from(calendarDayMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
};

export const CalendarContainer: FC<Props> = ({ shop }) => {
  const [calendarDays, setCalendarDays] = React.useState(shop.calendarDays);
  const [showsAllCalendarDays, setShowsAllCalendarDays] = React.useState(false);
  const [fetchCalendarDays, { loading }] =
    useShopCardLargeFooterCalendarContainer_ShopCalendarDaysLazyQuery();
  const hasMoreCalendarDays = !showsAllCalendarDays;

  React.useEffect(() => {
    setCalendarDays(shop.calendarDays);
    setShowsAllCalendarDays(false);
  }, [shop.calendarDays]);

  const handleLoadMoreCalendarDays = React.useCallback(async () => {
    setShowsAllCalendarDays(true);

    const { data } = await fetchCalendarDays({
      variables: {
        shopUuid: shop.uuid,
        daysAhead: ALL_CALENDAR_DAYS_FETCH_COUNT,
        daysOffset: calendarDays.length,
      },
    });

    const nextCalendarDays = data?.shop.calendarDays ?? [];

    setCalendarDays((currentCalendarDays) =>
      mergeCalendarDays(currentCalendarDays, nextCalendarDays)
    );
  }, [calendarDays.length, fetchCalendarDays, shop.uuid]);

  return (
    <Calendar
      shop={shop}
      calendarDays={calendarDays}
      hasMoreCalendarDays={hasMoreCalendarDays}
      isLoadingCalendarDays={loading}
      showsAllCalendarDays={showsAllCalendarDays}
      onLoadMoreCalendarDays={handleLoadMoreCalendarDays}
    />
  );
};
